import { Box } from "@mui/material"
import { useEffect, useRef } from "react"
import { confirm } from "lib/@dialogs/confirm"
import { useRefresh } from "lib/@hooks/useRefresh"
import { Bound } from "lib/@components/binding/Bound"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { normalizeLinkedFields, useComponentRef, hasLinkedFields } from "authoring/routes/macro/hooks/use-component-ref"
import { getMaintenanceTask } from "routes/component-library/controller/maintenance-library"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { getComponentRefAutoLinkMode } from "./component-ref-auto-link-mode"
import { resolveLinkedFields } from "authoring/routes/macro/lib/resolve-linked-fields"
import { useScheduleEditorActions } from "authoring/routes/project/schedule-editor/hooks/use-schedule-editor-actions"
import { useAmendsValue } from "authoring/routes/project/schedule-editor/hooks/use-amends-value"
import { getTaskAmendsKey } from "authoring/routes/project/amends/lib/task-amends-key"
import { normalizeAudienceAmends } from "authoring/routes/project/amends/lib/audience-aware-amends.mjs"

// Stable fallback so useComponentRef's useMemo([initialLinkedFields]) never
// sees a new object reference on each render when linkedFields is absent.
const EMPTY_LINKED_FIELDS = {}

/**
 * Tracks component-link state for the current task row and exposes it through
 * a nested Bound context so slot plugs can read componentRef/linkedFields
 * and re-render when they change.
 */
export function ComponentRefTaskWrapper({ children }) {
    const context = useBoundContext()
    const refresh = useRefresh()
    const task = context.task ?? context.target ?? null
    const componentRefRef = useRef(task?.componentRef ?? null)
    const linkedFieldsRef = useRef(normalizeLinkedFields(task?.linkedFields ?? EMPTY_LINKED_FIELDS))
    const originalTaskFieldsRef = useRef(null)
    const { componentRef, linkedFields, setComponentRef, enableField, disableField, setAllFields } = useComponentRef(
        task?.componentRef ?? null,
        task?.linkedFields ?? EMPTY_LINKED_FIELDS
    )

    // Tracks whether we need to auto-link fields once the component task loads
    const pendingAutoLink = useRef(false)
    const { saveAmend } = useScheduleEditorActions()
    const { amends } = useAmendsValue()
    const originalAuthorNoteRef = useRef(null)
    const { amendsContext } = context

    componentRefRef.current = componentRef
    linkedFieldsRef.current = linkedFields

    // Only fetch live component data in edit mode. Non-editing schedules
    // (submitted/rejected/approved) should display the values that were
    // resolved into the draft at submission time, not the latest component.
    const { editMode } = context
    const liveComponentId = editMode && componentRef ? componentRef : undefined
    const componentTask = getMaintenanceTask.useResults(liveComponentId, withInitialValue(IS_LOADING))

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(autoLinkAvailableFields, [componentTask])

    function autoLinkAvailableFields() {
        if (!pendingAutoLink.current) return
        if (!componentTask || componentTask === IS_LOADING) return

        pendingAutoLink.current = false
        overwriteAuthorNotesFromComponent(componentTask)

        const available = getAvailableFields(componentTask)
        if (!Object.keys(available).length) return

        linkedFieldsRef.current = available
        setAllFields(available)
        syncTaskLinkState(componentRefRef.current, available)
    }

    // Author notes are a per-review-cycle amends record, not a task field, so they
    // can't go through the linkedFields/FIELD_MAP mechanism used for title/content/
    // notes/etc. Linking a component is a deliberate wholesale replacement action —
    // mirror that for author notes by overwriting the task's current-cycle note
    // unconditionally, same as the other linked fields do. Saving through
    // amendsContext.onSaveAmends (rather than calling saveAmend directly) keeps
    // AmendsBoundary's own local editor state in sync, so the inline note shown on
    // the task row updates immediately rather than only the author notes tab.
    function overwriteAuthorNotesFromComponent(ct) {
        if (!ct?.authorsNotesHtml || !task) return
        const amendsKey = getTaskAmendsKey(task)
        if (!amendsKey) return
        if (originalAuthorNoteRef.current === null) {
            originalAuthorNoteRef.current = getCurrentAuthorNote(amendsKey)
        }
        saveTaskAuthorNote(amendsKey, { content: ct.authorsNotesHtml })
    }

    function getCurrentAuthorNote(amendsKey) {
        if (amendsContext) return amendsContext.getAmendsState(amendsKey).noteData
        const existing = amends.find((amend) => amend?.payload?.amendsKey === amendsKey)
        return normalizeAudienceAmends(existing?.payload ?? {})
    }

    function restoreOriginalAuthorNotes() {
        if (originalAuthorNoteRef.current === null || !task) return
        const amendsKey = getTaskAmendsKey(task)
        if (amendsKey) saveTaskAuthorNote(amendsKey, originalAuthorNoteRef.current)
        originalAuthorNoteRef.current = null
    }

    function saveTaskAuthorNote(amendsKey, noteData) {
        if (amendsContext) {
            amendsContext.onSaveAmends(amendsKey, noteData, { silent: true })
            return
        }
        if (saveAmend) saveAmend({ amendsKey, ...noteData, silent: true })
    }

    return (
        <Bound
            componentRef={componentRef}
            linkedFields={linkedFields}
            componentTask={componentTask}
            onSetComponentRef={handleSetComponentRef}
            onClearComponentRef={handleClearComponentRef}
            onEnableField={handleEnableField}
            onDisableField={handleDisableField}
        >
            <Box sx={{ display: "contents" }} onFocusCapture={handleFocusCapture}>
                {children}
            </Box>
        </Bound>
    )

    function handleFocusCapture() {
        context.onRegisterComponentTokenTarget?.({
            onComponentTokenInserted: handleComponentTokenInserted,
        })
    }

    function handleComponentTokenInserted(componentId) {
        if (!componentRef || componentRef === componentId) {
            handleSetComponentRef(componentId)
            return
        }

        confirm("Replace the existing component link with this new component?", "Replace component link?").then(
            (ok) => {
                if (ok) handleSetComponentRef(componentId)
            }
        )
    }

    function handleSetComponentRef(nextComponentRef) {
        const currentComponentRef = componentRefRef.current
        componentRefRef.current = nextComponentRef ?? null
        if (nextComponentRef && !currentComponentRef && task && !originalTaskFieldsRef.current) {
            originalTaskFieldsRef.current = captureTaskFields(task)
        }
        setComponentRef(nextComponentRef)
        if (nextComponentRef) {
            // Auto-enable all fields that the component has values for.
            // Users can then disconnect individual fields they don't want.
            pendingAutoLink.current = true

            const autoLinkMode = getComponentRefAutoLinkMode({
                currentComponentRef,
                nextComponentRef,
                hasLoadedComponentTask: Boolean(componentTask && componentTask !== IS_LOADING),
            })

            if (autoLinkMode === "auto-link-now") {
                autoLinkAvailableFields()
                return
            }

            if (autoLinkMode === "clear-and-wait") {
                linkedFieldsRef.current = normalizeLinkedFields({})
                setAllFields({})
                // Do not save the transient "componentRef + empty linkedFields" state.
                // The selected component should persist only once its auto-linkable
                // fields have been resolved and written together.
                refresh()
                return
            }

            return
        }
        syncTaskLinkState(componentRefRef.current, linkedFieldsRef.current)
    }

    function handleClearComponentRef() {
        componentRefRef.current = null
        linkedFieldsRef.current = normalizeLinkedFields({})
        setComponentRef(null)
        syncTaskLinkState(null, {})
        restoreOriginalAuthorNotes()
    }

    function handleEnableField(field) {
        const nextLinkedFields = normalizeLinkedFields({ ...linkedFieldsRef.current, [field]: true })
        linkedFieldsRef.current = nextLinkedFields
        enableField(field)
        syncTaskLinkState(componentRefRef.current, nextLinkedFields)
    }

    function handleDisableField(field) {
        const nextLinkedFields = normalizeLinkedFields({ ...linkedFieldsRef.current, [field]: false })
        linkedFieldsRef.current = nextLinkedFields
        disableField(field)
        syncTaskLinkState(componentRefRef.current, nextLinkedFields)
    }

    function getAvailableFields(ct) {
        const fields = {}
        if (ct.classification) fields.classification = true
        if (ct.skillSet) fields.skillSet = true
        if (ct.frequency || ct.frequencyCode) fields.frequency = true
        if (typeof ct.minutes === "number") fields.duration = true
        if (ct.heading) fields.title = true
        if (ct.actionsHtml) fields.content = true
        if (ct.notesHtml) fields.notes = true
        return fields
    }

    function syncTaskLinkState(nextComponentRef, nextLinkedFields) {
        if (task) {
            task.componentRef = nextComponentRef ?? null
            task.linkedFields = normalizeLinkedFields(nextLinkedFields)
            if (hasLinkedFields(nextLinkedFields)) {
                Object.assign(task, resolveLinkedFields(task, componentTask))
            } else if (originalTaskFieldsRef.current) {
                Object.assign(task, originalTaskFieldsRef.current)
                originalTaskFieldsRef.current = null
            }
        }
        // Call context.onChange directly rather than relying on refresh() to
        // propagate it.  useRefresh() freezes onChange via useCallback(…, []);
        // if the schedule editor's Bound had onChange=undefined on the first
        // render (saveReady starts false), the captured value stays undefined
        // forever and refresh() never triggers the save chain.
        refresh()
        context.onChange?.()
    }

    function captureTaskFields({ title, content, note, classification, minutes, skillSet, frequency }) {
        return {
            title,
            content,
            note,
            classification,
            minutes,
            skillSet,
            frequency: frequency ? { ...frequency } : frequency,
        }
    }
}
